import {combine, merge, sample, forward, guard} from 'effector';

import {DocumentType} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {PackageTypes} from 'types/gtins';
import {mapSortings, mapFilters} from 'utils/grid-mappers';

import {
    data$,
    fetchDataFx,
    filters$,
    filtersChanged,
    Gate,
    pageNumber$,
    pageSize$,
    clearDataCacheFx,
    sortingsChanged,
    filtersReset,
    defaultFilters,
    defaultSortings,
    sortings$,
    sortingsReset,
    tableStateChanged,
    tableStateCacheChanged,
    stateReset,
    searchChanged,
    search$,
    gridTokensCache$,
    gridCacheReset,
    gridCacheChanged,
    fetchProductsNamesFx,
    productsNames$,
    fetchStorageLocationsFx,
    storageLocations$,
    initWithAffixingFilters,
} from './index';

data$.on(fetchDataFx.done, (state, {result}) => result).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset);
pageSize$.reset(stateReset);
search$.reset(Gate.close);
filters$.reset(Gate.close);

forward({
    from: Gate.open,
    to: [fetchProductsNamesFx, fetchStorageLocationsFx],
});

productsNames$
    .on(fetchProductsNamesFx.done, (state, {result}) =>
        result.map((item, index) => ({
            id: index.toString(),
            value: item,
            label: item,
        })),
    )
    .reset(Gate.close);

storageLocations$
    .on(fetchStorageLocationsFx.done, (state, {result}) => {
        const entries = Object.keys(result);
        const mappedResult = entries.map((name, index) => ({
            id: index.toString(),
            value: result[name],
            label: name,
            text: result[name],
        }));
        return mappedResult;
    })
    .reset(Gate.close);

gridTokensCache$
    .on(fetchDataFx.done, (state, {params, result}) => ({
        ...state,
        [params.paging.pageNumber + 1]: result.nextPageRef ?? null,
    }))
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

forward({
    from: tableStateCacheChanged,
    to: clearDataCacheFx,
});

guard({
    source: Gate.open,
    filter: ({withAffixingFilters}) => Boolean(withAffixingFilters),
    target: initWithAffixingFilters,
});

sample({
    clock: initWithAffixingFilters,
    fn: () => [
        {id: 'packagingType', value: PackageTypes.GROUP},
        {id: 'status', value: DocumentType.ISSUED},
    ],
    target: filtersChanged,
});

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: combine({
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        filters: filters$,
        search: search$,
        cache: gridTokensCache$,
    }),
    fn: ({pageNumber, pageSize, filters, sortings, search, cache}) => ({
        paging: {pageNumber, pageSize},
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
        search,
        pageRef: cache?.[pageNumber] ?? null,
    }),
    target: fetchDataFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
